// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * P1 (founder-reported, 2026-09-22 — companion to the P0 server fix in
 * `services/planning/src/handlers/ledger.ts`'s `planCoversDate`; mirrors
 * `mobile/src/lib/planWeek.ts` exactly): the SAME truthful question — does
 * the plan on screen actually cover this date? — answered client-side from
 * the SAME field the server already serves (`PlanResponse.weekStartDate`,
 * `GET /v1/plans/current`, unchanged by this pass) rather than a new wire
 * field. A plan's week is exactly 7 days (`weekStartDate` ..
 * `weekStartDate + 6`, inclusive, ISO dates); a `date` outside that range
 * means the plan on screen has nothing to say about that day — distinct
 * from a genuinely fresh/broken account that has never generated a plan at
 * all (that case never reaches this helper: both Today and Plan already
 * short-circuit to their own `!plan` error state before any of this runs).
 *
 * Pure ISO-date arithmetic only (day granularity, UTC to stay DST-safe) —
 * the exact same shape as the server's own `addDaysIso`
 * (`services/planning/src/history/snapshot.ts`), never a re-derivation of
 * any floor/target/day-state arithmetic, which stays server-owned.
 */

function addDaysIso(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** The plan's own last covered day (`weekStartDate + 6`, inclusive). */
export function planWeekEndDate(weekStartDate: string): string {
  return addDaysIso(weekStartDate, 6)
}

/**
 * True when `date` falls within the plan's own 7-day week — the
 * client-side twin of the server's `planCoversDate` (ledger.ts).
 * ISO `YYYY-MM-DD` strings compare correctly with plain string comparison.
 */
export function planCoversDate(weekStartDate: string, date: string): boolean {
  return date >= weekStartDate && date <= planWeekEndDate(weekStartDate)
}
